import React from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './TrainCategories.css';

const TrainCategories = () => {
    const navigate = useNavigate();

    // Trainer category options - Heroes added first with bright pink theme
    const trainerCategories = [
        {
            id: 'hero-trainers',
            title: 'Hero Trainers',
            description: 'Train with legendary superheroes and unlock your potential',
            image: '/assets/images/hero-trainers.jpg',
            route: '/hero-trainers',
            color: '#FF1493', // Bright pink/red theme
            sound: 'gameIntroMode2',
            trainerCount: 6
        },
        {
            id: 'bodyweight-trainers',
            title: 'Bodyweight Trainers',
            description: 'Expert coaches specializing in bodyweight movement mastery',
            image: '/assets/images/bodyweight-trainers.jpg',
            route: '/bodyweight-trainers',
            color: '#0066CC', // Blue theme
            sound: 'bodyweightSplash2',
            trainerCount: 6
        },
        {
            id: 'advanced-bodyweight-trainers',
            title: 'Advanced Bodyweight Trainers',
            description: 'Elite coaches for advanced movement patterns and skills',
            image: '/assets/images/advanced-bodyweight-trainers.jpg',
            route: '/advanced-bodyweight-trainers',
            color: '#00FF66', // Bright green theme
            sound: 'advancedSplash2',
            trainerCount: 6
        },
        {
            id: 'strength-trainers',
            title: 'Strength Trainers',
            description: 'Powerful coaches for maximum strength development',
            image: '/assets/images/strength-trainers.jpg',
            route: '/strength-trainers',
            color: '#FF0000', // Red theme to match StrengthTrainers.css
            sound: 'strengthSplash2',
            trainerCount: 6
        },
        {
            id: 'bands-trainers',
            title: 'Resistance Band Trainers',
            description: 'Dynamic coaches for resistance band training excellence',
            image: '/assets/images/bands-trainers.jpg',
            route: '/bands-trainers',
            color: '#FFC107', // Yellow theme to match BandsTrainers.css
            sound: 'bandsSplash2',
            trainerCount: 6
        },
        {
            id: 'everything-trainers',
            title: 'Everything All Moves Trainers',
            description: 'Master coaches for comprehensive fitness training',
            image: '/assets/images/everything-trainers.jpg',
            route: '/everything-trainers',
            color: '#8E24AA', // Purple theme to match EverythingTrainers.css
            sound: 'everythingSplash2',
            trainerCount: 6
        }
    ];

    // Handle trainer category selection
    const handleCategorySelect = (category) => {
        console.log('Trainer category clicked:', category);

        // 🎵 PLAY CATEGORY-SPECIFIC SOUND
        if (category.sound) {
            soundManager.playSound(category.sound, 0.7);
        } else {
            soundManager.playButtonSound();
        }

        // Navigate to the specific trainer category page
        navigate(category.route);
    };

    // Handle back to home
    const handleBackToHome = () => {
        soundManager.playButtonSound();
        navigate('/');
    };

    return (
        <div className="train-categories-container">
            <header className="train-categories-header">
                <button
                    className="back-to-home-button"
                    onClick={handleBackToHome}
                >
                    ← Back to Home
                </button>

                <div className="header-content">
                    <h1>Choose Your Training Expert</h1>
                    <p>Select a category to find the perfect trainer for your fitness goals</p>
                </div>
            </header>

            <div className="trainer-categories-grid">
                {trainerCategories.map((category) => (
                    <div
                        key={category.id}
                        className="trainer-category-card-link"
                        onClick={() => handleCategorySelect(category)}
                    >
                        <div
                            className="trainer-category-card"
                            data-category-id={category.id}
                            style={{
                                '--theme-color': category.color
                            }}
                        >
                            <div className="trainer-category-image">
                                <div
                                    className="image-placeholder"
                                    style={{
                                        backgroundColor: category.color,
                                        backgroundImage: `url(${category.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="trainer-count-badge">
                                        {category.trainerCount} Trainers
                                    </div>
                                </div>
                            </div>
                            <div className="trainer-category-info">
                                <h2 style={{ color: category.color }}>{category.title}</h2>
                                <p>{category.description}</p>
                                <div className="category-features">
                                    <span className="feature-badge">
                                        📋 Preset Programs
                                    </span>
                                    <span className="feature-badge">
                                        📈 4 Intensity Levels
                                    </span>
                                    <span className="feature-badge">
                                        🏆 Expert Guidance
                                    </span>
                                </div>
                                <div className="theme-indicator" style={{ backgroundColor: category.color }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="train-categories-footer">
                <div className="train-info">
                    <h3>🎯 How Training Programs Work</h3>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-icon">👨‍🏫</span>
                            <div>
                                <strong>Expert Trainers</strong>
                                <p>Each trainer has unique specialties and 6-week programs</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <span className="info-icon">📊</span>
                            <div>
                                <strong>4 Intensity Levels</strong>
                                <p>Choose 100%, 75%, 50%, or 25% intensity to match your fitness</p>
                            </div>
                        </div>
                        <div className="info-item">
                            <span className="info-icon">📅</span>
                            <div>
                                <strong>Structured Programs</strong>
                                <p>Follow preset weekly progressions designed by experts</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flow-info">
                    <p>💡 <strong>Flow:</strong> Home → Train → Category → Trainer → Intensity → Workout</p>
                </div>
            </footer>
        </div>
    );
};

export default TrainCategories;